import React, { useState, useMemo } from 'react';
import api from '../services/api';
import Loading from '../components/Loading';
import ErrorToast from '../components/ErrorToast';
import Toast from '../components/Toast';
import '../styles/Common.css';
import './ImportarImoveis.css';

const ImportarImoveis = () => {
  const [modo, setModo] = useState('cepNumero');
  const [inputs, setInputs] = useState({ cep: '', numero: '', contribuinte: '' });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState([]); // sempre array
  const [pagina, setPagina] = useState(1);
  const pageSize = 10;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  // Estado para armazenar filtro de cada coluna
  const [columnFilters, setColumnFilters] = useState({
    NumeroContribuinte: '',
    NomeLogradouro: '',
    Numero: '',
    Complemento: '',
    Bairro: '',
    Referencia: '',
    Cep: '',
    AreaConstruida: '',
    TipoUso: '',
  });

  // Quando mudamos o modo de busca, reseta tudo
  const handleModoChange = (e) => {
    setModo(e.target.value);
    setInputs({ cep: '', numero: '', contribuinte: '' });
    setDados([]);
    setColumnFilters({
      NumeroContribuinte: '',
      NomeLogradouro: '',
      Numero: '',
      Complemento: '',
      Bairro: '',
      Referencia: '',
      Cep: '',
      AreaConstruida: '',
      TipoUso: '',
    });
    setPagina(1);
    setErro('');
  };

  // Atualiza campos de busca (exceto CEP, que tem máscara própria)
  const handleInput = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Máscara de CEP: 12345-678
  const handleCepChange = (e) => {
    let valor = e.target.value.replace(/\D/g, '');
    if (valor.length > 8) valor = valor.slice(0, 8);
    if (valor.length > 5) valor = valor.slice(0, 5) + '-' + valor.slice(5);
    setInputs((prev) => ({ ...prev, cep: valor }));
  };

  const buscar = async (e) => {
    e.preventDefault();
    if (modo === 'cepNumero') {
      const cepSemHifen = inputs.cep.replace('-', '');
      if (!cepSemHifen || !inputs.numero) {
        setErro('Preencha CEP e Número');
        return;
      }
    }
    if (modo === 'contribuinte' && !inputs.contribuinte) {
      setErro('Preencha Contribuinte');
      return;
    }

    setErro('');
    setLoading(true);
    try {
      const params =
        modo === 'cepNumero'
          ? { cep: inputs.cep, numero: inputs.numero }
          : { numero_contribuinte: inputs.contribuinte };

      const { data } = await api.get(
        `/bigquery/imoveis`,
        {
          params,
        }
      );
      setDados(Array.isArray(data) ? data : []);
      setPagina(1);

      // Limpa filtros de coluna após nova busca
      setColumnFilters({
        NumeroContribuinte: '',
        NomeLogradouro: '',
        Numero: '',
        Complemento: '',
        Bairro: '',
        Referencia: '',
        Cep: '',
        AreaConstruida: '',
        TipoUso: '',
      });
    } catch {
      setErro('Erro na consulta');
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar um filtro específico de coluna
  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setPagina(1); // Resetar página ao mudar filtro
  };

  // Filtra dados de acordo com columnFilters (substrings, não diferencia maiúscula/minúscula)
  const aplicados = useMemo(() => {
    return dados.filter((item) => {
      return Object.entries(columnFilters).every(([field, filtro]) => {
        if (!filtro) return true; // sem filtro → mantém
        const valorItem = item[field];
        if (valorItem == null) return false;
        // Converte para string e compara com includes (case-insensitive)
        return String(valorItem)
          .toLowerCase()
          .includes(String(filtro).toLowerCase());
      });
    });
  }, [dados, columnFilters]);

  const paginaDados = useMemo(() => {
    const arr = Array.isArray(aplicados) ? aplicados : [];
    const start = (pagina - 1) * pageSize;
    return arr.slice(start, start + pageSize);
  }, [aplicados, pagina]);

  const handleImport = async () => {
    setImportLoading(true);
    setErro('');
    try {
      const contribuintes = aplicados.map((item) => item.NumeroContribuinte);
      await api.post(
        `/processamentos/importar_imoveis`,
        { contribuintes },
      );
      setToastMsg(`Processo iniciado para ${contribuintes.length} imóveis.`);
      setConfirmOpen(false);
    } catch {
      setErro('Erro ao iniciar importação.');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Importar Imóveis</h1>
      {erro && <ErrorToast message={erro} onClose={() => setErro('')} />}
      {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg('')} />}

      {/* ───────────────────────────────────────────────────────────────────
          Seção de filtros de busca inicial (CEP/Número ou Contribuinte)
          ─────────────────────────────────────────────────────────────────── */}
      <div className="filters-section">
        <form onSubmit={buscar}>
          <div className="filters-grid">
            <div className="filter-item">
              <label>Modo:</label>
              <select value={modo} onChange={handleModoChange}>
                <option value="cepNumero">CEP + Número</option>
                <option value="contribuinte">Contribuinte</option>
              </select>
            </div>

            {modo === 'cepNumero' ? (
              <>
                <div className="filter-item">
                  <label>CEP:</label>
                  <input
                    name="cep"
                    value={inputs.cep}
                    onChange={handleCepChange}
                    placeholder="12345-678"
                  />
                </div>
                <div className="filter-item">
                  <label>Número:</label>
                  <input
                    name="numero"
                    placeholder="Número"
                    value={inputs.numero}
                    onChange={handleInput}
                  />
                </div>
              </>
            ) : (
              <div className="filter-item">
                <label>Contribuinte:</label>
                <input
                  name="contribuinte"
                  placeholder="Contribuinte"
                  value={inputs.contribuinte}
                  onChange={handleInput}
                />
              </div>
            )}

            <div className="filter-item filter-actions">
              <button type="submit" className="button">
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* ───────────────────────────────────────────────────────────────────
    Se houver resultados, exibe botão de importação, tabela e paginação
    ─────────────────────────────────────────────────────────────────── */}
      {loading ? (
        <Loading />
      ) : dados.length === 0 ? (
        <p style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          Nenhum imóvel encontrado.
        </p>
      ) : (
        <>
          {/* BOTÃO DE IMPORTAR FILTRADOS */}
          <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
            <button
              className="button"
              onClick={() => setConfirmOpen(true)}
              disabled={aplicados.length === 0 || importLoading}
            >
              {importLoading
                ? 'Carregando...'
                : `Importar ${aplicados.length} imóvel(is)`}
            </button>
          </div>

          {/* Tabela de resultados com filtro em cada coluna */}
          <div className="table-container table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Contribuinte</th>
                  <th>Logradouro</th>
                  <th>Nº</th>
                  <th>Compl.</th>
                  <th>Bairro</th>
                  <th>Ref.</th>
                  <th>CEP</th>
                  <th>Área Const.</th>
                  <th>Tipo Uso</th>
                </tr>
                <tr>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.NumeroContribuinte}
                      onChange={(e) =>
                        handleColumnFilterChange('NumeroContribuinte', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.NomeLogradouro}
                      onChange={(e) =>
                        handleColumnFilterChange('NomeLogradouro', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.Numero}
                      onChange={(e) =>
                        handleColumnFilterChange('Numero', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.Complemento}
                      onChange={(e) =>
                        handleColumnFilterChange('Complemento', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.Bairro}
                      onChange={(e) =>
                        handleColumnFilterChange('Bairro', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.Referencia}
                      onChange={(e) =>
                        handleColumnFilterChange('Referencia', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.Cep}
                      onChange={(e) =>
                        handleColumnFilterChange('Cep', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.AreaConstruida}
                      onChange={(e) =>
                        handleColumnFilterChange('AreaConstruida', e.target.value)
                      }
                    />
                  </th>
                  <th>
                    <input
                      className="input"
                      placeholder="Filtrar..."
                      value={columnFilters.TipoUso}
                      onChange={(e) =>
                        handleColumnFilterChange('TipoUso', e.target.value)
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginaDados.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.NumeroContribuinte}</td>
                    <td>{item.NomeLogradouro}</td>
                    <td>{item.Numero}</td>
                    <td>{item.Complemento}</td>
                    <td>{item.Bairro}</td>
                    <td>{item.Referencia}</td>
                    <td>{item.Cep}</td>
                    <td>{item.AreaConstruida}</td>
                    <td>{item.TipoUso}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="pagination">
            <button
              className="button"
              disabled={pagina === 1}
              onClick={() => setPagina((p) => p - 1)}
            >
              Anterior
            </button>
            <span>
              {pagina} / {Math.ceil(aplicados.length / pageSize)}
            </span>
            <button
              className="button"
              disabled={pagina * pageSize >= aplicados.length}
              onClick={() => setPagina((p) => p + 1)}
            >
              Próxima
            </button>
          </div>

          {/* Modal de confirmação */}
          {confirmOpen && (
            <div className="modal-overlay" onClick={() => setConfirmOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                {importLoading ? (
                  <Loading />
                ) : (
                  <>
                    <h3 className="page-title" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>
                      Confirmação
                    </h3>
                    <p>
                      Deseja importar <strong>{aplicados.length}</strong> imóvel(is)?
                    </p>
                    <div className="filter-actions">
                      <button
                        className="button"
                        onClick={handleImport}
                        disabled={importLoading}
                      >
                        Confirmar
                      </button>
                      <button
                        className="button"
                        onClick={() => setConfirmOpen(false)}
                        disabled={importLoading}
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default ImportarImoveis;
